import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Dialog, Text, TextInput } from '@opendoor/bricks-next';
import type { SavedSearchFieldsFragment, SavedSearchInput } from '__generated__/athena';
import type { TextInput as RNTextInput } from 'react-native';

import { toPropertySearchQuery, toSavedSearchInput } from './mapping';
import type { SavedSearchMutationResult } from './useSavedSearches';

export interface RenameSavedSearchDialogProps {
  savedSearch: SavedSearchFieldsFragment;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Renames via the host's shared `useSavedSearches` instance so the list the
   * dropdown renders refetches and updates in place (a separate instance would
   * only reflect the change on reload).
   */
  onRename: (id: string, input: SavedSearchInput) => Promise<SavedSearchMutationResult>;
}

const SAVE_SEARCH_NAME_MAX_LENGTH = 80;
const GENERIC_ERROR = 'Failed to rename search. Please try again.';

export const RenameSavedSearchDialog = ({
  savedSearch,
  isOpen,
  onClose,
  onRename,
}: RenameSavedSearchDialogProps) => {
  const [name, setName] = useState(savedSearch.name);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<RNTextInput | null>(null);

  // Reset name + error whenever a different saved search is opened for rename.
  useEffect(() => {
    if (isOpen) {
      setName(savedSearch.name);
      setErrorMessage(null);
      queueMicrotask(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen, savedSearch.name, savedSearch.id]);

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }
    onClose();
  }, [submitting, onClose]);

  const handleSubmit = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName || submitting) {
      return;
    }
    if (trimmedName === savedSearch.name) {
      onClose();
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    // Round-trip the full record through the mapping layer so unmapped
    // fields (max bed/bath, opendoor-owned, FHA, finite HOA cap) survive
    // the update. `toSavedSearchInput` receives `savedSearch` as the
    // preservation source.
    const query = toPropertySearchQuery(savedSearch);
    const input = toSavedSearchInput(query, trimmedName, savedSearch);
    const result = await onRename(savedSearch.id, input);
    setSubmitting(false);
    if (result.success) {
      onClose();
      return;
    }
    setErrorMessage(result.errorMessage ?? GENERIC_ERROR);
  }, [name, savedSearch, submitting, onRename, onClose]);

  const trimmedName = name.trim();
  const canSubmit = !submitting && trimmedName.length > 0;

  let submitLabel = 'Save';
  if (submitting) {
    submitLabel = 'Saving…';
  }

  return (
    <Dialog
      analyticsName="rename-saved-search-dialog"
      isOpen={isOpen}
      setIsOpen={handleClose}
      dismissable={!submitting}
    >
      <Box width="100%" $largerThanSM={{ width: 360 }}>
        <Dialog.Content
          title="Rename saved search"
          description="Update the label for this saved search."
        >
          <Box pt={16} pb={8}>
            <TextInput
              ref={inputRef}
              size="medium"
              title="Search name"
              value={name}
              onChangeText={setName}
              maxLength={SAVE_SEARCH_NAME_MAX_LENGTH}
              analyticsName="rename-saved-search-name-input"
              placeholder="My saved search"
            />
            {errorMessage && (
              <Text
                tag="p"
                typography="$bodySmall"
                color="$contentStateErrorDefault"
                role="alert"
                mt={8}
              >
                {errorMessage}
              </Text>
            )}
          </Box>
        </Dialog.Content>
        <Dialog.Actions
          buttonOneProps={{
            analyticsName: 'rename-saved-search-cancel',
            label: 'Cancel',
            variant: 'secondary',
            onPress: handleClose,
            disabled: submitting,
          }}
          buttonTwoProps={{
            analyticsName: 'rename-saved-search-submit',
            label: submitLabel,
            variant: 'primary',
            onPress: handleSubmit,
            disabled: !canSubmit,
            loading: submitting,
          }}
        />
      </Box>
    </Dialog>
  );
};
